import React, { createRef } from 'react'
import { renderReport } from '../components/elementList';
import FormComponent from '../components/formComponent';
import StepManager from '../components/stepManager'
import UiDeviceEntity from './uiDeviceEntity'

let documentation = {
    title: 'Add Device',
    brief: 'Adds a Device to Thingsboard',
    content: <p>Adds a device and its EntityViews to Thingsboard. Creates relations between the device's EntityViews and
         all the chosen Customer Types. Creates relations with the chosen Fields EntityViews. Assign the device Entity views to the chosen customer.</p>,
    steps: [
        {
            id: 0,
            title: 'Set Device Name',
            brief: 'Choose device Name and insert device Description',
            content: <p>Creates a device as a Thingsboard Device and one device EntityView per chosen customer type.
            The Name is automatically pre-pended with the selected field's name.
                    The description is automatically added.</p>
        },
        {
            id: 1,
            title: 'Add Device Attributes',
            brief: 'Adds the device latitude, longitude and area coordinates.',
            content: <p> <i> Latitude</i> and <i>Longitude</i> are the coordinates of the device and are displayed
                in the Acuadori dashboards as GPS point. Coordinates is the device controlled area. Can be inserted in the following form:
                <span className='text-info'> [[lat1, lon1], [latP2, lonP2], ...] </span>.
                Latitude, Longitude and Coordinates are inserted as Thingsboard Device attribute.</p>
        }
    ]
};

class UiDeviceMeteo extends React.Component {

    static defaultProps = {
        finished: () => { },
        DEVICETYPE: 'acuadori meteo',
        customerField: {},
        title: 'New Device'
    };

    constructor(props) {
        super(props);

        this.deviceService = props.services.device;
        this.customerService = props.services.customer;

        this.stepManagerRef = createRef();

        this.state = {
            device: null,
            attributes: null,
            deviceEntities: []
        };
    }

    documentation = () => documentation;

    makeSteps = () => {
        let { name, entities } = this.props.customerField;
        let { device } = this.state;

        let steps = [
            {
                stepId: 0, stepName: this.documentation().steps[0].title,
                formValue: this.renderDeviceNameForm, dataValue: () => renderReport({
                    "Name": device.name,
                    "Type": device.type,
                    'Id': device.id.id,
                    'Description': device.additionalInfo.description,
                }),
                documentation: this.documentation().steps[0]
            },
            {
                stepId: 1, stepName: this.documentation().steps[1].title,
                formValue: this.renderAddServerAttributes, dataValue: () => renderReport(this.reportDeviceAttributes()),
                documentation: this.documentation().steps[1]
            }
        ];

        for (let i = 0; i < entities.length; i++) {
            let item = entities[i];
            let step = {
                stepId: i + 2,
                stepName: `Connect ${item.name} Device to ${name}`,
                formValue: () => this.renderDeviceEntity(item),
                documentation: {
                    brief: `Creates the ${item.name} Device EntityView`,
                    content: <p>After creating the devices's EntityView, it is <i>assigned</i> to the corresponding customer and a
                <i> Contains relation</i> is created betwen the EntityView and the {item.type.name} field's EntityView.</p>
                },
            };
            steps.push(step);
        }
        return steps;
    }

    stepManagerAdvance = () => {
        let { device, deviceEntities } = this.state;
        const element = this.stepManagerRef.current;
        element.advance(() => {
            if (element.isEnd()) {
                this.props.finished({ device: device, deviceEntities: deviceEntities }, this.reportAllComponentData());
            }
        });
    };
    renderAlert = (message, variant) => {
        if (this.stepManagerRef.current)
            this.stepManagerRef.current.showAlert(message, variant);
    };
    handleSubmitError = (error) => {
        let message = 'Error: ' + error.message;
        this.renderAlert(message, 'warning');
    }
    handleValidate = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = 'Required';
        }
        return errors;
    };
    handleAttributesValidate = (values) => {
        const errors = {};

        ['latitude', 'longitude'].forEach((key) => {
            if (values[key] !== '' && !Number.isFinite(Number(values[key]))) {
                errors[key] = 'Must be a valid number';
            }
        });

        if (values.coordinates !== '') {
            try {
                const coordinates = JSON.parse(values.coordinates);
                const validCoordinates = Array.isArray(coordinates)
                    && coordinates.length > 0
                    && coordinates.every((point) =>
                        Array.isArray(point)
                        && point.length === 2
                        && point.every((value) => Number.isFinite(Number(value)))
                    );

                if (!validCoordinates) {
                    errors.coordinates = 'Use [[latitude, longitude], ...]';
                }
            }
            catch (error) {
                errors.coordinates = 'Use valid JSON: [[latitude, longitude], ...]';
            }
        }

        return errors;
    };

    handleDeviceNameSubmit = (values) => {
        let { customerField, DEVICETYPE } = this.props;
        values.name = `${customerField.name} ${values.name}`;
        values.type = DEVICETYPE;
        return this.deviceService.addDevice(values).then((response) => {
            this.setState({ device: response }, this.stepManagerAdvance);
            this.renderAlert('Device saved', 'success');
        }
        ).catch(this.handleSubmitError);
    }
    renderDeviceNameForm = () => {
        let { customerField } = this.props;

        return (
            <div>
                <FormComponent
                    initialValues={{ name: '', type: '', label: '', description: '' }}
                    validateFunc={this.handleValidate}
                    submitFunc={this.handleDeviceNameSubmit}
                    inputs={[
                        { key: 'name', type: 'text', inputType: 'input', placeholder: 'Name', label: 'Name', fixedPre: customerField.name },
                        { key: 'description', type: 'text', inputType: 'input', placeholder: 'Description', label: 'Description' }
                    ]}
                />
            </div>
        );
    }

    reportDeviceAttributes = () => {
        let { attributes } = this.state;
        if (!attributes) {
            return null;
        }

        let attr = [];
        for (let i = 0; i < attributes.length; i++) {
            let newVal = { key: attributes[i].key, value: attributes[i].value };
            attr.push(newVal);
        }
        return attr;
    }
    handleAttributesSubmit = (values) => {
        let { device } = this.state;
        const attributes = {
            latitude: values.latitude === '' ? '' : Number(values.latitude),
            longitude: values.longitude === '' ? '' : Number(values.longitude),
            coordinates: values.coordinates === ''
                ? ''
                : JSON.stringify(JSON.parse(values.coordinates))
        };

        return this.deviceService.addServerAttributes(attributes, device.id.id).then(
            () => {
                this.deviceService.getDeviceById(device.id.id).then(
                    (response) => {
                        let newDevice = response;
                        this.deviceService.getDeviceAttributesById(device.id.id).then(
                            (response) => {
                                let newAttributes = response;
                                this.setState({ device: newDevice, attributes: newAttributes },
                                    this.stepManagerAdvance);
                                return true;
                            }).catch(this.handleSubmitError);
                    }).catch(this.handleSubmitError);
            }).catch(this.handleSubmitError);
    };

    reportAllComponentData = () => {
        let { device, deviceEntities } = this.state;

        let report = [{ key: "Device", value: device.name }];
        let attributes = this.reportDeviceAttributes();
        attributes.forEach(item => {
            report.push(item)
        });
        deviceEntities.forEach((item => {
            report.push({ key: item.data.name, value: item.data.type });
        }))

        return report;
    }

    renderAddServerAttributes = () => {
        return (
            <div>
                <FormComponent
                    initialValues={{ latitude: '', longitude: '', coordinates: '' }}
                    validateFunc={this.handleAttributesValidate}
                    submitFunc={this.handleAttributesSubmit}
                    inputs={[
                        { key: 'latitude', type: 'text', inputType: 'input', placeholder: 'Latitude', label: 'Latitude' },
                        { key: 'longitude', type: 'text', inputType: 'input', placeholder: 'Longitude', label: 'Longitude' },
                        { key: 'coordinates', type: 'text', inputType: 'input', placeholder: 'Coordinates', label: 'Coordinates' }
                    ]}
                />
            </div>
        );
    }

    renderDeviceEntity = (fieldEntity) => {
        let { device } = this.state;
        return (
            <UiDeviceEntity
                finished={(deviceEntityData, report) => {
                    let { deviceEntities } = this.state;
                    deviceEntities.push({ data: deviceEntityData, report: report });
                    this.setState({ deviceEntities: deviceEntities });
                    this.stepManagerAdvance();
                }}
                error={this.handleSubmitError}
                services={{ device: this.deviceService, customer: this.customerService }}
                device={device}
                fieldEntity={fieldEntity}
                type={{ name: fieldEntity.type.name, fixedPre: fieldEntity.type.devicePre, fixedPost: fieldEntity.type.devicePost }}
            />
        );
    }

    render() {
        let { title } = this.props;
        let { finished } = this.state;
        let stepList = this.makeSteps();

        return (
            <div className="container">
                {!finished &&
                    <StepManager ref={this.stepManagerRef}
                        alert={{ active: true }}
                        progressBar={{ active: true }}

                        startStepId={0} endStepId={stepList.length - 1}

                        onFinished={() => {
                            //window.open( this.props.links.thingsboardRoutes.dashboards );
                            this.setState({ finished: true })
                        }}

                        stepList={stepList}

                        documentation={{
                            title: title,
                            subtitle: 'Adds a new device to Thingsboard Acuadori',
                            brief: this.documentation().brief,
                            content: this.documentation().text,
                        }}
                    />
                }
                {finished && renderReport()}
            </div>
        );
    }
}

export default UiDeviceMeteo;
export {
    UiDeviceMeteo,
    documentation
}
